import { DqIcon } from "@decentriq/components";
import { exceptions } from "@decentriq/utils";
import { faCheck, faExclamationCircle } from "@fortawesome/pro-light-svg-icons";
import {
  Alert,
  Box,
  Button,
  Chip,
  type ChipProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { memo, type ReactNode, useMemo } from "react";
import { DataRoomErrorReport } from "components";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import {
  type AudienceStatus,
  type AudienceV3,
} from "features/mediaDataRoom/models";
import { useAudiences } from "features/mediaDataRoom/wrappers";
import { mapMediaDataRoomErrorToSnackbar } from "hooks";

const audienceStatusPresentationMap = new Map<AudienceStatus, string>([
  ["published", "Ready and shared"],
  ["ready", "Ready"],
]);

type AudienceV3StatusChipProps = AudienceV3 & { extendedOnError?: boolean };

const AudienceV3StatusChip = memo<AudienceV3StatusChipProps>(
  ({ extendedOnError, ...audience }) => {
    const { id, status, isStale } = audience;
    const {
      audienceSizes: { audienceSizes, retrySizeEstimationForAudience },
    } = useAudiences();
    const { currentUserEmail } = useMediaDataRoom();
    const sizeState = audienceSizes[id];
    const error = useMemo<
      | {
          message: string | undefined;
          messageNode: ReactNode | undefined;
          details?: string;
          canRetry: boolean;
          canReport: boolean;
        }
      | undefined
    >(() => {
      if (isStale) {
        return {
          canReport: false,
          canRetry: false,
          message: audience.errorReport?.fullMessage,
          messageNode: audience.errorReport?.fullMessage,
        };
      }
      if (sizeState?.error) {
        const [message, details, messageNode] =
          sizeState.error instanceof exceptions.MediaDataRoomNoScoredUsersError
            ? [
                `The selected seed audience ${audience.name} is
                  too small to compute a lookalike audience.`,
                { context: sizeState.error.message },
                <span>
                  The selected seed audience{" "}
                  <strong style={{ fontWeight: 700 }}>{audience.name}</strong>{" "}
                  is too small to compute a lookalike audience.
                </span>,
              ]
            : [
                ...mapMediaDataRoomErrorToSnackbar(
                  sizeState.error,
                  "Unable to estimate audience size"
                ),
                undefined,
              ];
        return {
          canReport: true,
          canRetry: !(
            sizeState.error instanceof
            exceptions.MediaDataRoomNoScoredUsersError
          ),
          details: details?.context,
          message,
          messageNode: messageNode ?? message,
        };
      }
    }, [
      isStale,
      audience.errorReport?.fullMessage,
      sizeState?.error,
      audience.name,
    ]);
    const hasError = useMemo(() => Boolean(error?.message), [error?.message]);
    const tooltipTitle = useMemo<ReactNode | undefined>(
      () =>
        hasError
          ? error?.messageNode
          : status === "published" &&
              audience.sharedWith.length &&
              audience.createdBy === currentUserEmail
            ? `Audience is ready and has been shared with ${audience.sharedWith.map(({ name }) => name).join(", ")}`
            : undefined,
      [status, audience, hasError, error, currentUserEmail]
    );
    const hasTooltip = Boolean(tooltipTitle);
    const hasErrorActions = Boolean(error?.canRetry || error?.canReport);
    const isComputingSize = sizeState?.loading;
    const color = useMemo<ChipProps["color"]>(
      () => (hasError ? "danger" : isComputingSize ? "neutral" : "success"),
      [hasError, isComputingSize]
    );
    const label = useMemo(
      () =>
        isComputingSize
          ? "Computing"
          : hasError
            ? "Error"
            : audienceStatusPresentationMap.get(status),
      [isComputingSize, status, hasError]
    );
    return (
      <DataRoomErrorReport
        details={error?.details ?? ""}
        error={error?.message ?? ""}
        renderTrigger={(openErrorReportDialog) =>
          hasError && extendedOnError ? (
            <Alert color="danger">
              <Stack direction="column">
                {error?.messageNode}
                {hasErrorActions ? (
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Box>
                      {error?.canRetry && (
                        <Button
                          onClick={() =>
                            retrySizeEstimationForAudience(audience.id)
                          }
                          size="sm"
                          variant="soft"
                        >
                          Retry
                        </Button>
                      )}
                    </Box>
                    {error?.canReport && (
                      <Button
                        color="danger"
                        onClick={openErrorReportDialog}
                        size="sm"
                        variant="solid"
                      >
                        Report issue to Decentriq
                      </Button>
                    )}
                  </Stack>
                ) : undefined}
              </Stack>
            </Alert>
          ) : (
            <Tooltip
              title={
                hasTooltip ? (
                  <Stack direction="column">
                    <Typography level="body-sm" sx={{ color: "inherit" }}>
                      {tooltipTitle}
                    </Typography>
                    {hasErrorActions && (
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1}
                      >
                        <Box>
                          {error?.canRetry && (
                            <Button
                              onClick={() =>
                                retrySizeEstimationForAudience(audience.id)
                              }
                              size="sm"
                              variant="soft"
                            >
                              Retry
                            </Button>
                          )}
                        </Box>
                        {error?.canReport && (
                          <Button
                            color="danger"
                            onClick={openErrorReportDialog}
                            size="sm"
                            variant="solid"
                          >
                            Report issue to Decentriq
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                ) : undefined
              }
            >
              <Chip
                color={color}
                component="span"
                size="md"
                startDecorator={
                  <DqIcon
                    icon={
                      isComputingSize
                        ? undefined
                        : hasError
                          ? faExclamationCircle
                          : faCheck
                    }
                  />
                }
                sx={(theme) => ({
                  borderRadius: theme.radius.sm,
                })}
              >
                {label}
              </Chip>
            </Tooltip>
          )
        }
      />
    );
  }
);
AudienceV3StatusChip.displayName = "AudienceV3StatusChip";

export default AudienceV3StatusChip;
